import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import { useAppDispatch, useAppSelector } from '../store';
import { getResumen } from '../manager/workflow-instancia-estado-nodos.reducer';
import { fetchInboxLeidoCounts } from '../manager/profase-usuario-inbox.reducer';

// ── Mock data (replace with real selectors when backend is wired) ──────────

const kanbanData = [
  { name: 'Bloqueado', value: 1, color: '#94a3b8' },
  { name: 'Pendiente', value: 2, color: '#f59e0b' },
  { name: 'En Proceso', value: 2, color: '#3b82f6' },
  { name: 'Bajo Revisión', value: 2, color: '#a855f7' },
  { name: 'Hecho', value: 1, color: '#22c55e' },
];

const inboxData = [
  { dia: 'Lun', mensajes: 3 },
  { dia: 'Mar', mensajes: 7 },
  { dia: 'Mié', mensajes: 2 },
  { dia: 'Jue', mensajes: 5 },
  { dia: 'Vie', mensajes: 4 },
  { dia: 'Sáb', mensajes: 1 },
  { dia: 'Dom', mensajes: 0 },
];

/** Map API `color` hint to bar fill (hex). */
const RESUMEN_COLOR_MAP: Record<string, string> = {
  green: '#22c55e',
  gray: '#94a3b8',
  grey: '#94a3b8',
  yellow: '#eab308',
  red: '#ef4444',
  blue: '#3b82f6',
  skyblue: '#38bdf8',
};

function resumenRowsToChartData(rows: any[]) {
  return (rows ?? []).map((r: any) => ({
    estado: r.nodoEstado ?? '—',
    cantidad: Number(r.cantidad) || 0,
    fill: RESUMEN_COLOR_MAP[String(r.color ?? '').toLowerCase()] ?? '#64748b',
  }));
}

/** Unread count from `leido-counts` rows (`leido === false` → `cantidad`). */
function unreadCantidadFromLeidoCounts(rows: any[]): number {
  if (!Array.isArray(rows)) return 0;
  const row = rows.find((r: any) => r.leido === false);
  return Number(row?.cantidad) || 0;
}

// ── Sub-components ─────────────────────────────────────────────────────────

interface CardProps {
  title: string;
  subtitle: string;
  linkLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}

function DashboardCard({ title, subtitle, linkLabel, onClick, children }: CardProps) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <p style={styles.cardTitle}>{title}</p>
          <p style={styles.cardSubtitle}>{subtitle}</p>
        </div>
        <button style={styles.linkBtn} onClick={onClick}>
          {linkLabel} →
        </button>
      </div>
      <div style={styles.chartArea}>{children}</div>
    </div>
  );
}

// Custom pie label showing percentage
const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  if (percent < 0.06) return null;
  const RADIAN = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

export default function Home() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const resumen = useAppSelector((s: any) => s.workflowInstanciaEstadoNodos.resumen ?? []);
  const loadingResumen = useAppSelector((s: any) => s.workflowInstanciaEstadoNodos.loadingResumen);
  const leidoCounts = useAppSelector((s: any) => s.profaseUsuarioInbox?.leidoCounts ?? []);
  const loadingLeidoCounts = useAppSelector((s: any) => s.profaseUsuarioInbox?.loadingLeidoCounts ?? false);

  useEffect(() => {
    dispatch(getResumen());
    dispatch(fetchInboxLeidoCounts());
  }, [dispatch]);

  const mensajesSinLeer = useMemo(() => unreadCantidadFromLeidoCounts(leidoCounts), [leidoCounts]);

  const actividadesData = useMemo(() => resumenRowsToChartData(resumen), [resumen]);
  const actividadesVencidas = useMemo(
    () => resumen.find((r: any) => String(r.nodoEstado).toUpperCase() === 'VENCIDO')?.cantidad ?? 0,
    [resumen],
  );

  const onActividadesBarClick = (data: any) => {
    const payload = data?.payload ?? data;
    const estado = String(payload?.estado ?? '').toUpperCase();
    const p = new URLSearchParams();
    p.set('page', '1');
    if (estado === 'FINALIZADO') {
      p.set('sort', 'workflowEstado,asc');
      p.set('nodoEstado', 'FINALIZADO');
      navigate(`/actividades?${p.toString()}`);
      return;
    }
    if (estado === 'PENDIENTE') {
      p.set('nodoEstado', 'PENDIENTE');
      navigate(`/actividades?${p.toString()}`);
      return;
    }
    if (estado === 'VENCIDO') {
      p.set('sort', 'nodoEstado,asc');
      p.set('nodoEstado', 'VENCIDO');
      navigate(`/actividades?${p.toString()}`);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h2 style={styles.pageTitle}>Dashboard</h2>
        <p style={styles.pageSubtitle}>Resumen general del CRM</p>
      </div>

      {/* ── Stat strip ── */}
      <div style={styles.statStrip}>
        <StatBadge label="Tareas Pendientes" value={kanbanData.find(d => d.name === 'Pendiente')?.value ?? 0} color="#f59e0b" />
        <StatBadge label="En Proceso" value={kanbanData.find(d => d.name === 'En Proceso')?.value ?? 0} color="#3b82f6" />
        <StatBadge label="Mensajes sin leer" value={loadingLeidoCounts ? 0 : mensajesSinLeer} color="#8b5cf6" />
        <StatBadge label="Actividades vencidas" value={actividadesVencidas} color="#ef4444" />
      </div>

      {/* ── Chart cards ── */}
      <div style={styles.grid}>

        {/* Kanban pie */}
        <DashboardCard
          title="Tareas Kanban"
          subtitle="Distribución por estado"
          linkLabel="Ir al Kanban"
          onClick={() => navigate('/kanban')}
        >
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={kanbanData}
                cx="50%"
                cy="50%"
                outerRadius={85}
                dataKey="value"
                labelLine={false}
                label={renderPieLabel}
              >
                {kanbanData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value} tareas`} />
              <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </DashboardCard>

        {/* Inbox bar */}
        <DashboardCard
          title="Bandeja de Entrada"
          subtitle={
            loadingLeidoCounts
              ? 'Cargando mensajes…'
              : `${mensajesSinLeer} mensaje${mensajesSinLeer === 1 ? '' : 's'} sin leer`
          }
          linkLabel="Ir al Inbox"
          onClick={() => navigate('/inbox')}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={inboxData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="dia" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: 'rgba(139,92,246,0.08)' }}
                formatter={(v) => `${v} mensajes`}
              />
              <Bar dataKey="mensajes" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </DashboardCard>

        {/* Activities bar */}
        <DashboardCard
          title="Actividades"
          subtitle={loadingResumen ? 'Cargando…' : 'Por estado de nodo (workflow)'}
          linkLabel="Ir a Actividades"
          onClick={() => navigate('/actividades')}
        >
          <ResponsiveContainer width="100%" height={220}>
            {loadingResumen && actividadesData.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                Cargando actividades…
              </div>
            ) : actividadesData.length === 0 ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: 13 }}>
                Sin datos de actividades
              </div>
            ) : (
              <BarChart data={actividadesData} layout="vertical" barSize={18}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="estado" width={100} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => `${v} tareas`} />
                <Bar dataKey="cantidad" radius={[0, 4, 4, 0]} onClick={onActividadesBarClick}>
                  {actividadesData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.fill}
                      cursor={['FINALIZADO', 'PENDIENTE', 'VENCIDO'].includes(String(entry.estado).toUpperCase()) ? 'pointer' : 'default'}
                    />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </DashboardCard>

      </div>
    </div>
  );
}

// ── Stat badge ─────────────────────────────────────────────────────────────

function StatBadge({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{ ...styles.statBadge, borderLeft: `4px solid ${color}` }}>
      <span style={{ ...styles.statValue, color }}>{value}</span>
      <span style={styles.statLabel}>{label}</span>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  pageHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  pageTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a',
  },
  pageSubtitle: {
    margin: 0,
    fontSize: 13,
    color: '#64748b',
  },
  statStrip: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  statBadge: {
    background: '#fff',
    borderRadius: 10,
    padding: '14px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    flex: '1 1 160px',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: 500,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: 20,
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '20px 20px 16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  cardTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: '#0f172a',
  },
  cardSubtitle: {
    margin: 0,
    fontSize: 12,
    color: '#64748b',
  },
  chartArea: {
    marginTop: 4,
  },
  linkBtn: {
    background: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 12,
    fontWeight: 600,
    color: '#3b82f6',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'background 0.15s',
  },
};
